import type { SvgIconComponent } from "@mui/icons-material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
    alpha,
    Box,
    Button,
    ButtonBase,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "./dashboardFormatters";
import ProcessDurationDialog from "./ProcessDurationDialog";
import type {
    ProcessDashboardData,
    ProcessTrendStatistic,
    RecentActivity,
} from "../../api/bpm/processDashboard";

interface ProcessDashboardMetricsProps {
    data?: ProcessDashboardData;
    loading?: boolean;
    onTaskFilterClick?: (filter: DashboardTaskFilter) => void;
    onProcessFilterClick?: (filter: DashboardProcessFilter) => void;
    onProcessClick?: (processInstanceId: string) => void;
}

export interface DashboardTaskFilter {
    situation?: "ATRASADA" | "NO_PRAZO";
    dueDateFrom?: string;
    dueDateTo?: string;
    withoutDueDate?: boolean;
}

export interface DashboardProcessFilter {
    state?: "ACTIVE" | "COMPLETED" | "HAS_INCIDENTS";
    periodField?: "start" | "end" | "incident";
}

type MetricColor = "primary" | "error" | "warning" | "info" | "success";

const PROCESS_TREND_DAYS = 7;
const PROCESS_DURATION_LIMIT = 5;

const panelSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    height: "100%",
    boxSizing: "border-box",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
} as const;

const recentActivitiesCarouselSx = {
    display: { xs: "flex", lg: "grid" },
    gridTemplateColumns: { lg: "repeat(5, minmax(0, 1fr))" },
    gap: 1,
    maxWidth: "100%",
    minWidth: 0,
    overflowX: { xs: "auto", lg: "hidden" },
    overflowY: "hidden",
    pb: { xs: 1, lg: 0 },
    scrollBehavior: "smooth",
    scrollSnapType: { xs: "x mandatory", lg: "none" },
    overscrollBehaviorX: "contain",
    scrollbarWidth: "thin",
    "& > *": {
        flex: {
            xs: "0 0 calc(100% - 32px)",
            sm: "0 0 calc(50% - 4px)",
            md: "0 0 calc(33.333% - 6px)",
            lg: "initial",
        },
        minWidth: 0,
        maxWidth: "100%",
        scrollSnapAlign: "start",
    },
} as const;

export default function ProcessDashboardMetrics({
    data,
    loading = false,
    onTaskFilterClick,
    onProcessFilterClick,
    onProcessClick,
}: ProcessDashboardMetricsProps) {
    const { t, i18n } = useTranslation();
    const processDurations = data?.processDurations ?? [];
    const [processDurationDialogOpen, setProcessDurationDialogOpen] = useState(false);
    const recentActivitiesCarouselRef = useRef<HTMLDivElement>(null);
    const rankedProcessDurations = useMemo(
        () => [...processDurations].sort(
            (left, right) =>
                (right.averageDurationInMillis ?? 0) - (left.averageDurationInMillis ?? 0),
        ),
        [processDurations],
    );
    const topProcessDurations = rankedProcessDurations.slice(0, PROCESS_DURATION_LIMIT);
    const recentActivities = (data?.recentActivities ?? []).slice(0, 5);
    const processTrend = (data?.processTrend ?? []).slice(-PROCESS_TREND_DAYS);
    const maxProcessDuration = Math.max(
        ...processDurations.map((process) => process.averageDurationInMillis ?? 0),
        1,
    );
    const maxTrendCount = getTrendScaleMax(processTrend);
    const slaTrackedTasks = (data?.slaCompliantTasksCount ?? 0) + (data?.slaViolatedTasksCount ?? 0);

    return (
        <Stack gap={2.5} sx={{ minWidth: 0, maxWidth: "100%" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                }}
            >
                <SummaryCard
                    title={t("dashboard.processMetrics.totalProcesses")}
                    value={data?.totalInstances}
                    icon={AccountTreeOutlinedIcon}
                    color="primary"
                    loading={loading}
                    tooltip={t("dashboard.processMetrics.totalProcessesHint")}
                    onClick={onProcessFilterClick ? () => onProcessFilterClick({}) : undefined}
                />
                <SummaryCard
                    title={t("dashboard.processMetrics.inProgressProcesses")}
                    value={data?.activeInstances}
                    icon={PlayCircleOutlineOutlinedIcon}
                    color="info"
                    loading={loading}
                    tooltip={t("dashboard.processMetrics.inProgressProcessesHint")}
                    onClick={onProcessFilterClick ? () => onProcessFilterClick({ state: "ACTIVE" }) : undefined}
                />
                <SummaryCard
                    title={t("dashboard.processMetrics.completedProcesses")}
                    value={data?.completedInstances}
                    icon={CheckCircleOutlineOutlinedIcon}
                    color="success"
                    loading={loading}
                    tooltip={t("dashboard.processMetrics.completedProcessesHint")}
                    onClick={onProcessFilterClick ? () => onProcessFilterClick({ state: "COMPLETED", periodField: "end" }) : undefined}
                />
            </Box>

            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper
                        elevation={0}
                        sx={{ ...panelSx, display: "flex", flexDirection: "column" }}
                    >
                        <Typography variant="h6" fontWeight={600} mb={0.5}>
                            {t("dashboard.processMetrics.processEvolution")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2.5}>
                            {t("dashboard.processMetrics.processEvolutionDescription")}
                        </Typography>
                        {loading ? (
                            <Stack gap={2}>{[1, 2, 3, 4].map((item) => <Skeleton key={item} height={58} />)}</Stack>
                        ) : processTrend.length ? (
                            <DailyTrendChart
                                points={processTrend}
                                maxValue={maxTrendCount}
                                locale={i18n.language}
                                startedLabel={t("dashboard.processMetrics.started")}
                                completedLabel={t("dashboard.processMetrics.completedTrend")}
                                getAccessibleLabel={(date, started, completed) => t(
                                    "dashboard.processMetrics.dailyTrendLabel",
                                    { date, started, completed },
                                )}
                            />
                        ) : (
                            <Typography color="text.secondary">
                                {t("dashboard.processMetrics.noEvolutionData")}
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                    <Paper elevation={0} sx={panelSx}>
                        <Typography variant="h6" fontWeight={600} mb={0.5}>
                            {t("dashboard.processMetrics.deadlinesAndSla")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2.5}>
                            {t("dashboard.processMetrics.deadlinesAndSlaDescription")}
                        </Typography>
                        {loading ? (
                            <Stack gap={2}>{[1, 2, 3, 4].map((item) => <Skeleton key={item} height={42} />)}</Stack>
                        ) : (
                            <Stack gap={2}>
                                <AttentionLine
                                    label={t("dashboard.processMetrics.myPendingTasks")}
                                    value={String(data?.pendingTasksCount ?? 0)}
                                    color="primary.main"
                                    onClick={() => onTaskFilterClick?.({})}
                                />
                                <AttentionLine
                                    label={t("dashboard.processMetrics.overdueTasks")}
                                    value={String(data?.overdueTasksCount ?? 0)}
                                    color={(data?.overdueTasksCount ?? 0) > 0 ? "error.main" : "text.primary"}
                                    onClick={() => onTaskFilterClick?.({ situation: "ATRASADA" })}
                                />
                                <AttentionLine
                                    label={t("dashboard.processMetrics.nearDeadlineWithPeriod", {
                                        count: data?.nearDeadlineDays ?? 7,
                                    })}
                                    value={String(data?.tasksNearDeadlineCount ?? 0)}
                                    color="warning.main"
                                    onClick={() => onTaskFilterClick?.(
                                        buildNearDeadlineTaskFilter(data?.nearDeadlineDays ?? 7),
                                    )}
                                />
                                <AttentionLine
                                    label={t("dashboard.processMetrics.tasksWithoutDueDate")}
                                    value={String(data?.tasksWithoutDueDateCount ?? 0)}
                                    color="text.secondary"
                                    onClick={() => onTaskFilterClick?.({ withoutDueDate: true })}
                                />
                                <Divider />
                                {data?.oldestActiveProcess?.processInstanceId ? (
                                    <OldestProcessAttention
                                        label={t("dashboard.processMetrics.oldestActiveProcess")}
                                        processName={data.oldestActiveProcess.processDefinitionName
                                            || data.oldestActiveProcess.processDefinitionKey
                                            || t("dashboard.processMetrics.unnamedProcess")}
                                        age={formatDuration(data.oldestActiveProcess.ageInMillis)}
                                        onClick={() => onProcessClick?.(
                                            data.oldestActiveProcess?.processInstanceId || "",
                                        )}
                                    />
                                ) : (
                                    <MetricLine
                                        label={t("dashboard.processMetrics.oldestActiveProcess")}
                                        value={t("dashboard.processMetrics.noActiveProcess")}
                                    />
                                )}
                                <Divider />
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" gap={2} mb={0.75}>
                                        <Typography variant="body2">{t("dashboard.processMetrics.taskSla")}</Typography>
                                        <Typography variant="body2" fontWeight={700}>{data?.slaComplianceRate ?? 0}%</Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={data?.slaComplianceRate ?? 0}
                                        color={(data?.slaComplianceRate ?? 0) >= 80 ? "success" : "warning"}
                                        sx={{ height: 7, borderRadius: 4 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {t("dashboard.processMetrics.slaSummary", {
                                            compliant: data?.slaCompliantTasksCount ?? 0,
                                            total: slaTrackedTasks,
                                        })}
                                    </Typography>
                                </Box>
                                <MetricLine
                                    label={t("dashboard.processMetrics.averagePendingWait")}
                                    value={formatDuration(data?.averagePendingTaskWaitInMillis)}
                                />
                                <Divider />
                                <Box>
                                    <Typography variant="body2" fontWeight={600} mb={1}>
                                        {t("dashboard.processMetrics.backlogByAge")}
                                    </Typography>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                                        <BacklogAgeItem label={t("dashboard.processMetrics.upTo7Days")} value={data?.pendingTasksUpTo7DaysCount ?? 0} />
                                        <BacklogAgeItem label={t("dashboard.processMetrics.from8To15Days")} value={data?.pendingTasksFrom8To15DaysCount ?? 0} />
                                        <BacklogAgeItem label={t("dashboard.processMetrics.from16To30Days")} value={data?.pendingTasksFrom16To30DaysCount ?? 0} />
                                        <BacklogAgeItem label={t("dashboard.processMetrics.over30Days")} value={data?.pendingTasksOver30DaysCount ?? 0} />
                                    </Box>
                                </Box>
                            </Stack>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={0} sx={panelSx}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.25} mb={2}>
                    <Stack direction="row" alignItems="flex-start" gap={1.25} minWidth={0}>
                        <HistoryOutlinedIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box minWidth={0}>
                            <Typography variant="h6" fontWeight={600} mb={0.25}>
                                {t("dashboard.processMetrics.recentActivities")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                {t("dashboard.processMetrics.recentActivitiesDescription")}
                            </Typography>
                        </Box>
                    </Stack>
                    {recentActivities.length > 1 ? (
                        <Stack direction="row" sx={{ display: { xs: "flex", lg: "none" }, flexShrink: 0 }}>
                            <IconButton
                                size="small"
                                aria-label={t("dashboard.processMetrics.previousActivities")}
                                onClick={() => scrollRecentActivities(recentActivitiesCarouselRef.current, -1)}
                            >
                                <ChevronLeftOutlinedIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                aria-label={t("dashboard.processMetrics.nextActivities")}
                                onClick={() => scrollRecentActivities(recentActivitiesCarouselRef.current, 1)}
                            >
                                <ChevronRightOutlinedIcon />
                            </IconButton>
                        </Stack>
                    ) : null}
                </Stack>

                {loading ? (
                    <Box
                        data-testid="recent-activities-carousel"
                        sx={recentActivitiesCarouselSx}
                    >
                        {[1, 2, 3, 4, 5].map((item) => <Skeleton key={item} height={92} />)}
                    </Box>
                ) : recentActivities.length ? (
                    <Box
                        ref={recentActivitiesCarouselRef}
                        data-testid="recent-activities-carousel"
                        sx={recentActivitiesCarouselSx}
                    >
                        {recentActivities.map((activity, index) => (
                            <RecentActivityItem
                                key={`${activity.type}-${activity.processInstanceId}-${activity.occurredAt}-${index}`}
                                activity={activity}
                                locale={i18n.language}
                                onClick={activity.processInstanceId
                                    ? () => onProcessClick?.(activity.processInstanceId || "")
                                    : undefined}
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {t("dashboard.processMetrics.noRecentActivities")}
                    </Typography>
                )}
            </Paper>

            <Paper elevation={0} sx={panelSx}>
                <Typography variant="h6" fontWeight={600} mb={0.5}>
                    {t("dashboard.processMetrics.operationalDetails")}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2.5}>
                    {t("dashboard.processMetrics.operationalDetailsDescription")}
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                        gap: 1.5,
                    }}
                >
                    <CompactMetric
                        label={t("dashboard.processMetrics.documents")}
                        value={data?.documentsCount ?? 0}
                        icon={DescriptionOutlinedIcon}
                        color="info"
                        loading={loading}
                        tooltip={t("dashboard.processMetrics.documentsHint")}
                    />
                    <CompactMetric
                        label={t("dashboard.processMetrics.signatures")}
                        value={data?.signaturesCount ?? 0}
                        icon={DrawOutlinedIcon}
                        color="success"
                        loading={loading}
                        tooltip={t("dashboard.processMetrics.signaturesHint")}
                    />
                    <CompactMetric
                        label={t("dashboard.processMetrics.incidents")}
                        value={data?.incidentsCount ?? 0}
                        icon={ReportProblemOutlinedIcon}
                        color="warning"
                        loading={loading}
                        secondary={t("dashboard.processMetrics.affectedProcesses", {
                            count: data?.incidentProcessesCount ?? 0,
                        })}
                        tooltip={t("dashboard.processMetrics.incidentsHint")}
                        onClick={onProcessFilterClick ? () => onProcessFilterClick({
                            state: "HAS_INCIDENTS",
                            periodField: "incident",
                        }) : undefined}
                    />
                </Box>

                <Divider sx={{ my: 2.5 }} />

                <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <DetailMetricCard
                            label={t("dashboard.processMetrics.totalDuration")}
                            value={loading ? "-" : formatDuration(data?.totalDurationInMillis)}
                            icon={AccessTimeOutlinedIcon}
                            color="primary"
                            loading={loading}
                            tooltip={t("dashboard.processMetrics.totalDurationHint")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <DetailMetricCard
                            label={t("dashboard.processMetrics.averageProcessDuration")}
                            value={loading ? "-" : formatDuration(data?.averageProcessDurationInMillis)}
                            icon={TimerOutlinedIcon}
                            color="success"
                            loading={loading}
                            tooltip={t("dashboard.processMetrics.averageProcessDurationHint")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <DetailMetricCard
                            label={t("dashboard.processMetrics.averageTaskDuration")}
                            value={loading ? "-" : formatDuration(data?.averageTaskDurationInMillis)}
                            icon={HourglassBottomOutlinedIcon}
                            color="info"
                            loading={loading}
                            tooltip={t("dashboard.processMetrics.averageTaskDurationHint")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <DetailMetricCard
                            label={t("dashboard.processMetrics.reworkRate")}
                            value={loading ? "-" : `${data?.reworkRate ?? 0}%`}
                            secondary={t("dashboard.processMetrics.reworkedProcesses", {
                                count: data?.reworkedInstancesCount ?? 0,
                            })}
                            icon={ReplayOutlinedIcon}
                            color="warning"
                            loading={loading}
                            tooltip={t("dashboard.processMetrics.reworkRateHint")}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, lg: 7 }}>
                        <Box mb={2.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t("dashboard.processMetrics.processDuration")}
                                </Typography>
                                {processDurations.length > PROCESS_DURATION_LIMIT ? (
                                    <Button
                                        size="small"
                                        onClick={() => setProcessDurationDialogOpen(true)}
                                    >
                                        {t("dashboard.processMetrics.viewAll")}
                                    </Button>
                                ) : null}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {t("dashboard.processMetrics.processDurationDescription")}
                            </Typography>
                        </Box>

                        {loading ? (
                            <Stack gap={2}>{[1, 2, 3].map((item) => <Skeleton key={item} height={52} />)}</Stack>
                        ) : processDurations.length ? (
                            <Stack gap={2.25}>
                                {topProcessDurations.map((process) => {
                                    const duration = process.averageDurationInMillis ?? 0;
                                    return (
                                        <Box key={process.processDefinitionKey || process.processDefinitionName}>
                                            <Stack direction="row" justifyContent="space-between" gap={2} mb={0.75}>
                                                <Typography variant="body2" fontWeight={600} noWrap>
                                                    {process.processDefinitionName
                                                        || process.processDefinitionKey
                                                        || t("dashboard.processMetrics.unnamedProcess")}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                                                    {formatDuration(duration)} · {t("dashboard.processMetrics.completedExecutions", {
                                                        count: process.completedExecutions ?? 0,
                                                    })}
                                                </Typography>
                                            </Stack>
                                            <LinearProgress
                                                variant="determinate"
                                                value={(duration / maxProcessDuration) * 100}
                                                sx={{ height: 7, borderRadius: 4 }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Typography color="text.secondary">
                                {t("dashboard.processMetrics.noProcessDurationData")}
                            </Typography>
                        )}
                    </Grid>

                    <Grid size={{ xs: 12, lg: 5 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                            {t("dashboard.processMetrics.myProductivity")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2.5}>
                            {t("dashboard.processMetrics.myProductivityDescription")}
                        </Typography>
                        {loading ? (
                            <Stack gap={2}>{[1, 2, 3].map((item) => <Skeleton key={item} height={48} />)}</Stack>
                        ) : (
                            <Stack gap={2}>
                                <MetricLine
                                    label={t("dashboard.processMetrics.averageCompletedTasksPerDay")}
                                    value={formatMetricNumber(
                                        data?.averageCompletedTasksPerDay ?? 0,
                                        i18n.language,
                                    )}
                                    tooltip={t("dashboard.processMetrics.averageCompletedTasksPerDayHint")}
                                />
                                <MetricLine
                                    label={t("dashboard.processMetrics.completionRate")}
                                    value={`${data?.completionRate ?? 0}%`}
                                    tooltip={t("dashboard.processMetrics.completionRateHint")}
                                />
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <ProcessDurationDialog
                open={processDurationDialogOpen}
                processes={rankedProcessDurations}
                onClose={() => setProcessDurationDialogOpen(false)}
            />
        </Stack>
    );
}

function SummaryCard({
    title,
    value,
    icon: Icon,
    color,
    loading,
    onClick,
    tooltip,
}: {
    title: string;
    value: number | string | undefined;
    icon: SvgIconComponent;
    color: MetricColor;
    loading: boolean;
    onClick?: () => void;
    tooltip?: string;
}) {
    return (
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <ButtonBase
                disabled={!onClick || loading}
                onClick={onClick}
                aria-label={title}
                sx={{ p: 2, width: "100%", height: "100%", justifyContent: "flex-start", textAlign: "left" }}
            >
                <Stack direction="row" alignItems="center" gap={1.5} minWidth={0} maxWidth="100%">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover", color: `${color}.main`, display: "flex", flexShrink: 0 }}>
                        <Icon fontSize="small" />
                    </Box>
                    <Box minWidth={0}>
                        <Tooltip title={tooltip || ""} arrow disableHoverListener={!tooltip}>
                            <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ cursor: tooltip ? "help" : "default" }}>
                                {title}
                            </Typography>
                        </Tooltip>
                        {loading ? (
                            <Skeleton width={72} height={34} />
                        ) : (
                            <Typography variant="h5" fontWeight={700} noWrap>{value ?? 0}</Typography>
                        )}
                    </Box>
                </Stack>
            </ButtonBase>
        </Paper>
    );
}

function CompactMetric({
    label,
    value,
    icon: Icon,
    color,
    loading,
    onClick,
    tooltip,
    secondary,
}: {
    label: string;
    value: number;
    icon: SvgIconComponent;
    color: MetricColor;
    loading: boolean;
    onClick?: () => void;
    tooltip?: string;
    secondary?: string;
}) {
    const content = (
        <>
            <Stack direction="row" alignItems="center" gap={1.25} minWidth={0}>
                <Icon sx={{ color: `${color}.main`, fontSize: 22 }} />
                <Box minWidth={0}>
                    <Tooltip title={tooltip || ""} arrow disableHoverListener={!tooltip}>
                        <Typography variant="body2" color="text.secondary" sx={{ cursor: tooltip ? "help" : "default", overflowWrap: "anywhere" }}>{label}</Typography>
                    </Tooltip>
                    {secondary ? <Typography variant="caption" color="text.secondary" display="block">{secondary}</Typography> : null}
                </Box>
            </Stack>
            {loading ? <Skeleton width={28} /> : <Typography variant="h6" fontWeight={700}>{value}</Typography>}
        </>
    );
    const sx = { p: 1.5, bgcolor: "action.hover", borderRadius: 2, minHeight: 66, minWidth: 0, maxWidth: "100%", overflow: "hidden" };

    if (onClick) {
        return (
            <ButtonBase
                onClick={onClick}
                disabled={loading}
                aria-label={label}
                sx={{ ...sx, width: "100%", display: "flex", justifyContent: "space-between", gap: 2, textAlign: "left" }}
            >
                {content}
            </ButtonBase>
        );
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={sx}
        >
            {content}
        </Stack>
    );
}

function BacklogAgeItem({ label, value }: { label: string; value: number }) {
    return (
        <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: "action.hover", minWidth: 0, overflow: "hidden" }}>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>{label}</Typography>
            <Typography variant="body1" fontWeight={700}>{value}</Typography>
        </Box>
    );
}

function DetailMetricCard({
    label,
    value,
    secondary,
    icon: Icon,
    color,
    loading,
    tooltip,
}: {
    label: string;
    value: string;
    secondary?: string;
    icon: SvgIconComponent;
    color: MetricColor;
    loading: boolean;
    tooltip?: string;
}) {
    const labelContent = (
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 0, overflowWrap: "anywhere" }}
        >
            {label}
        </Typography>
    );

    return (
        <Paper
            elevation={0}
            sx={(theme) => ({
                p: 2,
                height: "100%",
                minWidth: 0,
                maxWidth: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: alpha(theme.palette[color].main, 0.22),
                bgcolor: alpha(theme.palette[color].main, 0.045),
            })}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                <Box minWidth={0}>
                    {tooltip ? (
                        <Tooltip title={tooltip} arrow>
                            <Box component="span" sx={{ cursor: "help" }}>{labelContent}</Box>
                        </Tooltip>
                    ) : labelContent}
                    {loading ? (
                        <Skeleton width={82} height={36} />
                    ) : (
                        <Typography variant="h5" fontWeight={700} mt={0.5} noWrap>
                            {value}
                        </Typography>
                    )}
                    {!loading && secondary ? (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                            {secondary}
                        </Typography>
                    ) : null}
                </Box>
                <Box
                    sx={(theme) => ({
                        p: 1,
                        display: "flex",
                        flexShrink: 0,
                        borderRadius: 2,
                        color: `${color}.main`,
                        bgcolor: alpha(theme.palette[color].main, 0.12),
                    })}
                >
                    <Icon fontSize="small" />
                </Box>
            </Stack>
        </Paper>
    );
}

function MetricLine({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
    return (
        <Stack direction="row" justifyContent="space-between" gap={2} minWidth={0} maxWidth="100%">
            {tooltip ? (
                <Tooltip title={tooltip} arrow>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                    >
                        {label}
                    </Typography>
                </Tooltip>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{label}</Typography>
            )}
            <Typography variant="body2" fontWeight={600} whiteSpace="nowrap" flexShrink={0}>{value}</Typography>
        </Stack>
    );
}

function AttentionLine({
    label,
    value,
    color,
    onClick,
}: {
    label: string;
    value: string;
    color: string;
    onClick: () => void;
}) {
    return (
        <ButtonBase
            aria-label={label}
            onClick={onClick}
            sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                p: 1,
                borderRadius: 2,
                textAlign: "left",
                justifyContent: "space-between",
                "&:hover": { bgcolor: "action.hover" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{label}</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="h6" fontWeight={700} color={color}>{value}</Typography>
                <ArrowForwardIosOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            </Stack>
        </ButtonBase>
    );
}

function OldestProcessAttention({
    label,
    processName,
    age,
    onClick,
}: {
    label: string;
    processName: string;
    age: string;
    onClick: () => void;
}) {
    return (
        <ButtonBase
            aria-label={`${label}: ${processName}`}
            onClick={onClick}
            sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                p: 1,
                borderRadius: 2,
                textAlign: "left",
                justifyContent: "space-between",
                gap: 2,
                "&:hover": { bgcolor: "action.hover" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
            }}
        >
            <Box minWidth={0}>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>
                    {processName}
                </Typography>
            </Box>
            <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
                <Typography variant="body2" fontWeight={700}>{age}</Typography>
                <ArrowForwardIosOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            </Stack>
        </ButtonBase>
    );
}

function RecentActivityItem({
    activity,
    locale,
    onClick,
}: {
    activity: RecentActivity;
    locale?: string;
    onClick?: () => void;
}) {
    const { t } = useTranslation();
    const processName = activity.processDefinitionName
        || activity.processDefinitionKey
        || t("dashboard.processMetrics.unnamedProcess");
    const activityLabel = getRecentActivityLabel(activity, t);
    const ActivityIcon = getRecentActivityIcon(activity.type);

    return (
        <ButtonBase
            aria-label={t("dashboard.processMetrics.openRecentActivity", {
                activity: activityLabel,
                process: processName,
            })}
            disabled={!onClick}
            onClick={onClick}
            sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                minHeight: 92,
                p: 1.5,
                borderRadius: 2,
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: 1.25,
                textAlign: "left",
                bgcolor: "action.hover",
                "&:hover": { bgcolor: "action.selected" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
                "&.Mui-disabled": { opacity: 1 },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexShrink: 0,
                    p: 0.75,
                    borderRadius: 1.5,
                    color: getRecentActivityColor(activity.type),
                    bgcolor: "background.paper",
                }}
            >
                <ActivityIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box minWidth={0}>
                <Typography variant="body2" fontWeight={600} noWrap>
                    {activityLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {activity.type === "TASK_COMPLETED" && activity.taskName
                        ? `${activity.taskName} · ${processName}`
                        : processName}
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                    {formatRelativeActivityTime(activity.occurredAt, locale)}
                </Typography>
            </Box>
        </ButtonBase>
    );
}

function getRecentActivityLabel(
    activity: RecentActivity,
    t: ReturnType<typeof useTranslation>["t"],
) {
    if (activity.type === "PROCESS_STARTED") {
        return t("dashboard.processMetrics.processStartedActivity");
    }
    if (activity.type === "PROCESS_COMPLETED") {
        return t("dashboard.processMetrics.processCompletedActivity");
    }
    if (activity.type === "TASK_COMPLETED") {
        return t("dashboard.processMetrics.taskCompletedActivity");
    }
    return t("dashboard.processMetrics.unknownActivity");
}

function getRecentActivityIcon(type?: RecentActivity["type"]) {
    if (type === "PROCESS_STARTED") return PlayCircleOutlineOutlinedIcon;
    if (type === "PROCESS_COMPLETED") return CheckCircleOutlineOutlinedIcon;
    return TaskAltOutlinedIcon;
}

function getRecentActivityColor(type?: RecentActivity["type"]) {
    if (type === "PROCESS_STARTED") return "info.main";
    if (type === "PROCESS_COMPLETED") return "success.main";
    return "primary.main";
}

function formatRelativeActivityTime(value?: string, locale?: string) {
    if (!value) return "-";
    const occurredAt = new Date(value);
    if (Number.isNaN(occurredAt.getTime())) return "-";

    const differenceInSeconds = Math.round((occurredAt.getTime() - Date.now()) / 1_000);
    const absoluteDifference = Math.abs(differenceInSeconds);
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

    if (absoluteDifference < 60) return formatter.format(differenceInSeconds, "second");
    if (absoluteDifference < 3_600) return formatter.format(Math.round(differenceInSeconds / 60), "minute");
    if (absoluteDifference < 86_400) return formatter.format(Math.round(differenceInSeconds / 3_600), "hour");
    return formatter.format(Math.round(differenceInSeconds / 86_400), "day");
}

function buildNearDeadlineTaskFilter(days: number): DashboardTaskFilter {
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days);

    return {
        situation: "NO_PRAZO",
        dueDateFrom: formatDateInput(startDate),
        dueDateTo: formatDateInput(endDate),
    };
}

function formatDateInput(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function scrollRecentActivities(container: HTMLDivElement | null, direction: -1 | 1) {
    if (!container) return;
    const distance = Math.max(container.clientWidth * 0.8, 240);
    container.scrollBy?.({ left: distance * direction, behavior: "smooth" });
}

function DailyTrendChart({
    points,
    maxValue,
    locale,
    startedLabel,
    completedLabel,
    getAccessibleLabel,
}: {
    points: ProcessTrendStatistic[];
    maxValue: number;
    locale?: string;
    startedLabel: string;
    completedLabel: string;
    getAccessibleLabel: (date: string, started: number, completed: number) => string;
}) {
    const scaleTicks = [maxValue, maxValue * 0.75, maxValue * 0.5, maxValue * 0.25, 0];

    return (
        <Box
            data-testid="daily-trend-chart"
            sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 250, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
        >
            <Stack direction="row" gap={2.5} justifyContent="flex-end" mb={1.5}>
                <TrendLegend label={startedLabel} color="primary.main" />
                <TrendLegend label={completedLabel} color="success.main" />
            </Stack>
            <Box sx={{ flex: 1, minHeight: 220, overflowX: "auto" }}>
                <Box
                    sx={{
                        display: "flex",
                        height: "100%",
                        minHeight: 220,
                        minWidth: Math.max(points.length * 64, 420),
                    }}
                >
                    <Stack
                        aria-hidden
                        justifyContent="space-between"
                        sx={{ width: 34, flexShrink: 0, pb: "26px", pr: 1 }}
                    >
                        {scaleTicks.map((tick) => (
                            <Typography
                                key={tick}
                                variant="caption"
                                color="text.disabled"
                                lineHeight={1}
                                textAlign="right"
                            >
                                {tick}
                            </Typography>
                        ))}
                    </Stack>

                    <Box sx={{ position: "relative", flex: 1 }}>
                        <Stack
                            aria-hidden
                            justifyContent="space-between"
                            sx={{ position: "absolute", inset: 0, bottom: "26px" }}
                        >
                            {scaleTicks.map((tick) => (
                                <Divider key={tick} sx={{ borderColor: "divider", opacity: 0.7 }} />
                            ))}
                        </Stack>

                        <Stack direction="row" sx={{ position: "absolute", inset: 0, zIndex: 1 }}>
                            {points.map((point, index) => {
                                const started = point.startedInstances ?? 0;
                                const completed = point.completedInstances ?? 0;
                                const date = formatTrendDay(point.startDate, locale);

                                return (
                                    <Stack
                                        data-testid="daily-trend-day"
                                        key={point.startDate || index}
                                        alignItems="center"
                                        sx={{ flex: 1, minWidth: 64, height: "100%" }}
                                    >
                                        <Tooltip
                                            title={getAccessibleLabel(date, started, completed)}
                                            placement="top"
                                            arrow
                                        >
                                            <Box
                                                role="img"
                                                aria-label={getAccessibleLabel(date, started, completed)}
                                                sx={{
                                                    display: "flex",
                                                    flex: 1,
                                                    alignItems: "flex-end",
                                                    justifyContent: "center",
                                                    gap: 0.75,
                                                    width: "100%",
                                                    px: 1,
                                                }}
                                            >
                                                <TrendBar value={started} maxValue={maxValue} color="primary" />
                                                <TrendBar value={completed} maxValue={maxValue} color="success" />
                                            </Box>
                                        </Tooltip>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={500}
                                            height={26}
                                            lineHeight="26px"
                                        >
                                            {date}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function TrendBar({
    value,
    maxValue,
    color,
}: {
    value: number;
    maxValue: number;
    color: "primary" | "success";
}) {
    return (
        <Box
            sx={(theme) => ({
                width: { xs: 13, sm: 18 },
                maxWidth: 20,
                height: `${getTrendBarHeight(value, maxValue)}%`,
                minHeight: value === 0 ? 3 : 8,
                borderRadius: "7px 7px 2px 2px",
                background: `linear-gradient(180deg, ${theme.palette[color].light} 0%, ${theme.palette[color].main} 100%)`,
                boxShadow: value > 0 ? `0 4px 10px ${alpha(theme.palette[color].main, 0.22)}` : "none",
                transition: "transform 160ms ease, filter 160ms ease",
                "&:hover": {
                    filter: "brightness(1.06)",
                    transform: "translateY(-2px)",
                },
            })}
        />
    );
}

function TrendLegend({ label, color }: { label: string; color: string }) {
    return (
        <Stack direction="row" alignItems="center" gap={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Stack>
    );
}

function getTrendBarHeight(value: number, maxValue: number) {
    return value === 0 ? 0 : Math.max(4, (value / maxValue) * 100);
}

function getTrendScaleMax(points: ProcessTrendStatistic[]) {
    const highestValue = Math.max(
        ...points.flatMap((point) => [point.startedInstances ?? 0, point.completedInstances ?? 0]),
        1,
    );

    return Math.max(4, Math.ceil(highestValue / 4) * 4);
}

function formatTrendDay(startDate?: string, locale?: string) {
    if (!startDate) return "-";
    const formatter = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" });
    return formatter.format(new Date(startDate));
}

function formatMetricNumber(value: number, locale?: string) {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
}
